import { useState } from 'react';

import type { Finding, FindingSeverity, StreamStats } from '@/types';
import { fmtMbps, fmtMs } from '@/lib/format';
import { tosLabel } from '@/lib/trafficProfiles';

// Severity → colour + glyph. Deliberately restrained: an analysis panel that
// shouts at the operator gets ignored, and "good" findings matter as much as
// bad ones — proving prioritisation WORKS is the sellable result.
const STYLE: Record<FindingSeverity, { color: string; glyph: string }> = {
  bad: { color: 'var(--loss)', glyph: '✕' },
  warn: { color: 'var(--warn)', glyph: '!' },
  good: { color: 'var(--signal)', glyph: '✓' },
  info: { color: 'var(--muted)', glyph: 'i' },
};

export function findingColor(sev?: FindingSeverity | null): string {
  return STYLE[sev ?? 'info']?.color ?? 'var(--muted)';
}

/**
 * The numbered badge that ties a finding to the thing it is talking about.
 * The same component renders in the analysis list, next to a summary readout,
 * on a traffic-class row, and on a chart marker — so a reader who sees ② on a
 * jitter readout can scan back to finding ② without re-deriving anything.
 */
export function FindingBadge({
  n,
  severity,
  title,
}: {
  n: number;
  severity: FindingSeverity;
  title?: string;
}) {
  return (
    <span
      title={title}
      className="mono"
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        minWidth: 16,
        height: 16,
        padding: '0 3px',
        marginLeft: 5,
        borderRadius: 8,
        fontSize: 10,
        fontWeight: 700,
        lineHeight: 1,
        color: '#0b0e13',
        background: findingColor(severity),
        verticalAlign: 'middle',
      }}
    >
      {n}
    </span>
  );
}

/** Findings that reference a given summary field, with their 1-based numbers. */
export function findingsForField(
  findings: Finding[] | null | undefined,
  field: string,
): { n: number; f: Finding }[] {
  return (findings ?? [])
    .map((f, i) => ({ n: i + 1, f }))
    .filter(({ f }) => (f.summary_fields ?? []).includes(field));
}

/** Findings that reference a given stream. */
function findingsForStream(findings: Finding[], streamId: string) {
  return findings
    .map((f, i) => ({ n: i + 1, f }))
    .filter(({ f }) => (f.stream_ids ?? []).includes(streamId));
}

/** Renders the badges for a summary readout. Nothing when unreferenced. */
export function FieldBadges({
  findings,
  field,
}: {
  findings?: Finding[] | null;
  field: string;
}) {
  const hits = findingsForField(findings, field);
  if (hits.length === 0) return null;
  return (
    <>
      {hits.map(({ n, f }) => (
        <FindingBadge key={f.code + n} n={n} severity={f.severity} title={f.title} />
      ))}
    </>
  );
}

function FindingRow({ f, n }: { f: Finding; n: number }) {
  const [open, setOpen] = useState(false);
  const st = STYLE[f.severity] ?? STYLE.info;
  const evidence = Object.entries(f.evidence ?? {}).filter(
    ([, v]) => v !== null && v !== undefined && typeof v !== 'object',
  );

  return (
    <div style={{ borderLeft: `3px solid ${st.color}`, padding: '10px 0 10px 12px', marginBottom: 10 }}>
      <div className="row" style={{ gap: 8, alignItems: 'baseline' }}>
        <FindingBadge n={n} severity={f.severity} />
        <span aria-hidden style={{ color: st.color, fontWeight: 700 }}>{st.glyph}</span>
        <strong style={{ color: st.color }}>{f.title}</strong>
        {f.confidence === 'medium' && (
          <span
            className="mono"
            style={{ fontSize: 11, color: 'var(--muted)' }}
            title="Inferred from related measurements rather than measured directly"
          >
            inferred
          </span>
        )}
      </div>
      <div style={{ marginTop: 4, lineHeight: 1.5 }}>{f.detail}</div>
      {evidence.length > 0 && (
        <>
          <button
            className="btn"
            style={{ marginTop: 8, fontSize: 12, padding: '2px 8px' }}
            onClick={() => setOpen((o) => !o)}
          >
            {open ? 'Hide' : 'Show'} evidence
          </button>
          {open && (
            <div className="mono" style={{ fontSize: 12, color: 'var(--muted)', marginTop: 8 }}>
              {evidence.map(([k, v]) => (
                <div key={k}>
                  {k} = {typeof v === 'number' ? Number(v).toFixed(2) : String(v)}
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

/**
 * Per-traffic-class breakdown. In a concurrent mix the session-wide numbers
 * average across classes and hide the very thing being tested — "total
 * throughput was fine" while voice was unusable. This table is what makes the
 * QoS finding above auditable.
 */
function StreamTable({ rows, findings }: { rows: StreamStats[]; findings: Finding[] }) {
  return (
    <div style={{ overflowX: 'auto' }}>
      <table className="table" style={{ minWidth: 620 }}>
        <thead>
          <tr>
            <th>Traffic</th>
            <th>QoS</th>
            <th>Throughput avg</th>
            <th>Latency p95</th>
            <th>Jitter</th>
            <th>Loss</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((s) => {
            const hits = findingsForStream(findings, s.stream_id);
            return (
              <tr key={s.stream_id}>
                <td>
                  {s.traffic_type ?? `${s.protocol}/${s.direction}`}
                  <span className="mono" style={{ color: 'var(--muted)', marginLeft: 6, fontSize: 11 }}>
                    {s.protocol}·{s.direction}
                  </span>
                  {hits.map(({ n, f }) => (
                    <FindingBadge key={f.code + n} n={n} severity={f.severity} title={f.title} />
                  ))}
                </td>
                <td className="mono" style={{ fontSize: 12 }}>{tosLabel(s.tos ?? 0)}</td>
                <td>{fmtMbps(s.throughput_avg_mbps ?? null)}</td>
                <td>{fmtMs(s.latency_p95_ms ?? null)}</td>
                <td>{fmtMs(s.jitter_avg_ms ?? null)}</td>
                <td>{s.loss_avg_pct == null ? '—' : `${s.loss_avg_pct.toFixed(2)}%`}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export function FindingsPanel({
  findings,
  perStream,
}: {
  findings?: Finding[] | null;
  perStream?: StreamStats[] | null;
}) {
  const list = findings ?? [];
  const streams = (perStream ?? []).filter((s) => s.sample_count > 0);

  if (list.length === 0 && streams.length < 2) return null;

  return (
    <div className="card" style={{ marginBottom: 18 }}>
      <div className="eyebrow" style={{ marginBottom: 10 }}>analysis</div>

      {list.length > 0 ? (
        <>
          {list.map((f, i) => (
            <FindingRow key={f.code + f.title} f={f} n={i + 1} />
          ))}
          <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 4 }}>
            Numbers mark where each finding came from — look for the matching badge on the
            summary readouts, the traffic-class rows, and the charts below.
          </div>
        </>
      ) : (
        <div style={{ color: 'var(--muted)' }}>
          Nothing notable to report — no rule found enough evidence to draw a conclusion.
        </div>
      )}

      {streams.length > 1 && (
        <>
          <div className="eyebrow" style={{ margin: '18px 0 10px' }}>per traffic class</div>
          <StreamTable rows={streams} findings={list} />
        </>
      )}
    </div>
  );
}
